import { useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Popper,
  Typography,
  type PopperPlacementType,
} from "@mui/material";

/**
 * A single anchored coach mark: title, body, and a dismiss button.
 *
 * The caller resolves the anchor (see anchors.ts) and passes the
 * element in, so whoever is choosing *which* mark to show can also
 * tell whether it can actually be placed — a hint whose anchor is off
 * screen must not silently occupy the queue.
 */
interface Props {
  anchorEl: HTMLElement;
  title: string;
  body: string;
  placement?: PopperPlacementType;
  dismissLabel?: string;
  onDismiss: () => void;
  /** Fired once the mark has been on screen long enough to have been
   * read, so the caller can record it as seen even without a click.
   * Long enough to actually read the body — a hint dismissed before
   * the user has taken it in is worse than one that lingers. */
  onSeen?: () => void;
  seenAfterMs?: number;
}

export default function CoachMark({
  anchorEl,
  title,
  body,
  placement = "bottom-start",
  dismissLabel = "Got it",
  onDismiss,
  onSeen,
  seenAfterMs = 15000,
}: Props) {
  useEffect(() => {
    if (!onSeen) return;
    const timer = setTimeout(onSeen, seenAfterMs);
    return () => clearTimeout(timer);
  }, [onSeen, seenAfterMs]);

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement={placement}
      sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
      modifiers={[
        { name: "offset", options: { offset: [0, 12] } },
        // Some anchors fill the viewport (the canvas surface), so any
        // outside placement would land off screen. Allow the mark to
        // detach from its anchor rather than disappear.
        {
          name: "preventOverflow",
          options: { altAxis: true, tether: false, padding: 16 },
        },
        {
          name: "flip",
          options: { fallbackPlacements: ["bottom", "top", "right", "left"] },
        },
      ]}
    >
      <Paper
        elevation={6}
        sx={{
          p: 2,
          maxWidth: 340,
          border: 1,
          borderColor: "secondary.main",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {/* Body copy carries the actual instruction, so it reads at the
            same strength as the title. The muted default is translucent
            white in dark mode, which loses too much against an elevated
            Paper. */}
        <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
          {body}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button size="small" onClick={onDismiss}>
            {dismissLabel}
          </Button>
        </Box>
      </Paper>
    </Popper>
  );
}
